import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import AdminLayout from '../../layouts/AdminLayout'
import { CATEGORIES } from '../../constants/categories'
import { categoryService } from '@services/api'

const AdminCategoriesPage = () => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [isAddMode, setIsAddMode] = useState(false)
  const [formData, setFormData] = useState({
    libele_categorie: '',
    icon: '',
    couleur: '#D4AF37'
  })

  const getCurrentUserRoles = () => {
    const storedUser = localStorage.getItem('admin_user') || localStorage.getItem('user')
    if (!storedUser) return []
    try {
      const userData = JSON.parse(storedUser)
      const normalize = (r) => (typeof r === 'string' ? r : r?.name ?? '')

      if (Array.isArray(userData.roles)) return userData.roles.map(normalize)
      if (typeof userData.roles === 'string') return userData.roles.split(',').map(r => r.trim())
      if (userData.role) return [normalize(userData.role)]
    } catch (e) {}
    return []
  }

  const userRoles = getCurrentUserRoles()
  const hasAccess = userRoles.length === 0 || userRoles.some(r => {
    const name = r.toLowerCase()
    return name === 'super admin' || name === 'admin' || name === 'superviseur'
  })

  // Si pas d'accès, afficher message d'erreur
  if (!hasAccess) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Accès refusé</h2>
            <p className="text-gray-600">Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
            <p className="text-sm text-gray-500 mt-2">Rôles autorisés : Super Admin, Admin, Superviseur</p>
          </div>
        </div>
      </AdminLayout>
    )
  }
  
  // Retourne un nom Material Icons valide, sinon 'category'
  const safeIcon = (icon) => /^[a-z0-9_]+$/.test(icon || '') ? icon : 'category'

  const iconOptions = [
    // Navigation & Actions
    'home', 'menu', 'search', 'settings', 'dashboard', 'apps', 'more_vert', 'more_horiz',
    'arrow_back', 'arrow_forward', 'arrow_upward', 'arrow_downward', 'close', 'check',
    'add', 'remove', 'edit', 'delete', 'save', 'cancel', 'refresh', 'share',
    
    // Commerce & Shopping
    'shopping_cart', 'shopping_bag', 'store', 'storefront', 'local_mall', 'payment',
    'credit_card', 'account_balance', 'receipt', 'attach_money', 'euro', 'paid',
    
    // Communication
    'phone', 'smartphone', 'email', 'mail', 'message', 'chat', 'forum', 'comment',
    'contact_phone', 'contacts', 'call', 'send', 'notifications', 'campaign',
    
    // Technology & Devices
    'computer', 'laptop', 'tablet', 'phone_android', 'phone_iphone', 'watch',
    'headset', 'headphones', 'speaker', 'tv', 'desktop_windows', 'keyboard',
    'mouse', 'memory', 'storage', 'router', 'usb', 'cable', 'bluetooth', 'wifi',
    
    // Transportation
    'directions_car', 'directions_bike', 'directions_bus', 'directions_subway',
    'local_shipping', 'local_taxi', 'flight', 'train', 'directions_boat',
    'motorcycle', 'electric_car', 'electric_scooter', 'agriculture', 'construction',
    
    // Real Estate & Places
    'apartment', 'business', 'domain', 'factory', 'warehouse', 'landscape',
    'location_city', 'holiday_village', 'cottage', 'house', 'night_shelter',
    'gite', 'cabin', 'bungalow', 'villa', 'key', 'door_front', 'meeting_room',
    
    // Food & Dining
    'restaurant', 'fastfood', 'local_cafe', 'local_bar', 'local_pizza',
    'lunch_dining', 'dinner_dining', 'breakfast_dining', 'brunch_dining',
    'bakery_dining', 'liquor', 'wine_bar', 'coffee', 'cake', 'kitchen',
    'microwave', 'oven', 'countertops', 'blender', 'coffee_maker',
    
    // Fashion & Accessories
    'checkroom', 'style', 'face', 'diamond', 'spa', 'dry_cleaning',
    'local_laundry_service', 'iron', 'shield', 'watch_later', 'favorite',
    
    // Entertainment & Media
    'movie', 'theaters', 'music_note', 'album', 'library_music', 'piano',
    'sports_esports', 'casino', 'videogame_asset', 'games', 'toys',
    'sports_soccer', 'sports_basketball', 'sports_tennis', 'sports_football',
    'sports_volleyball', 'sports_baseball', 'sports_hockey', 'sports_cricket',
    'fitness_center', 'pool', 'golf_course', 'skateboarding', 'surfing',
    
    // Health & Medical
    'local_hospital', 'medical_services', 'health_and_safety', 'medication',
    'vaccines', 'healing', 'emergency', 'local_pharmacy', 'psychology',
    'monitor_heart', 'bloodtype', 'sanitizer', 'masks', 'science',
    
    // Education & Work
    'school', 'class', 'book', 'library_books', 'menu_book', 'auto_stories',
    'work', 'business_center', 'badge', 'card_membership', 'engineering',
    'architecture', 'biotech', 'calculate', 'functions', 'draw',
    
    // Home & Garden
    'lightbulb', 'light', 'lamp', 'ceiling', 'bed', 'chair', 'table_bar',
    'deck', 'garage', 'yard', 'grass', 'local_florist', 'park', 'eco',
    'energy_savings_leaf', 'compost', 'potted_plant', 'nature', 'forest',
    
    // Tools & Services
    'build', 'handyman', 'construction', 'plumbing', 'electrical_services',
    'roofing', 'carpenter', 'cleaning_services', 'home_repair_service',
    'pest_control', 'fire_extinguisher', 'security', 'lock', 'key',
    
    // Art & Creativity
    'palette', 'brush', 'photo_camera', 'camera', 'photo', 'collections',
    'image', 'wallpaper', 'gradient', 'texture', 'auto_awesome', 'star',
    'stars', 'emoji_events', 'celebration', 'attractions',
    
    // Weather & Nature
    'wb_sunny', 'nights_stay', 'cloud', 'ac_unit', 'thunderstorm', 'water_drop',
    'opacity', 'thermostat', 'wind_power', 'air', 'foggy', 'beach_access',
    
    // Business & Office
    'print', 'fax', 'scanner', 'description', 'folder', 'folder_open',
    'insert_drive_file', 'assignment', 'event', 'today', 'date_range',
    
    // Social & People
    'person', 'people', 'group', 'family_restroom', 'escalator_warning',
    'child_friendly', 'baby_changing_station', 'wc', 'accessible',
    
    // Animals
    'pets', 'cruelty_free', 'bug_report', 'agriculture',
    
    // Food Items
    'local_pizza', 'egg', 'ramen_dining', 'rice_bowl', 'soup_kitchen',
    'tapas', 'kebab_dining', 'food_bank', 'icecream', 'set_meal',
    
    // Miscellaneous
    'inventory', 'category', 'label', 'local_offer', 'new_releases',
    'verified', 'workspace_premium', 'military_tech', 'card_giftcard',
    'redeem', 'loyalty', 'percent', 'discount', 'sell', 'token'
  ]

  useEffect(() => {
    loadCategories()
  }, [])

  const loadCategories = async () => {
    try {
      const result = await categoryService.getCategories()
      if (result?.success !== false) {
        const dbCategories = result.data || []
        
        // CrÃ©er un map des catÃ©gories de la DB par nom
        const dbCategoryMap = {}
        dbCategories.forEach(cat => {
          dbCategoryMap[cat.libele_categorie] = cat
        })
        
        // Utiliser la liste CATEGORIES comme source principale
        // et enrichir avec les donnÃ©es de la DB si elles existent
        const enrichedCategories = CATEGORIES
          .filter(catName => catName !== 'Toutes les catégories')
          .map(catName => {
            if (dbCategoryMap[catName]) {
              // CatÃ©gorie existe dans la DB
              return dbCategoryMap[catName]
            } else {
              // CatÃ©gorie n'existe pas encore dans la DB, crÃ©er un objet temporaire
              return {
                categorie_id: `temp_${catName}`,
                libele_categorie: catName,
                icon: 'category',
                couleur: '#9e9e9e',
                isNew: true // Marqueur pour indiquer qu'elle n'est pas encore dans la DB
              }
            }
          })
        
        setCategories(enrichedCategories)
      }
    } catch (error) {
      console.error('Error loading categories:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleViewCategory = (category) => {
    setSelectedCategory(category)
    // Si c'est une nouvelle catÃ©gorie (pas encore en DB), on passe en mode ajout
    setIsAddMode(category.isNew || false)
    setFormData({
      libele_categorie: category.libele_categorie,
      icon: safeIcon(category.icon),
      couleur: category.couleur || '#D4AF37'
    })
    setShowModal(true)
  }
  
  const handleAddCategory = () => {
    setSelectedCategory(null)
    setIsAddMode(true)
    setFormData({
      libele_categorie: '',
      icon: 'category',
      couleur: '#D4AF37'
    })
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setSelectedCategory(null)
    setIsAddMode(false)
    setFormData({
      libele_categorie: '',
      icon: '',
      couleur: '#D4AF37'
    })
  }
  
  const handleSubmit = async () => {
    if (!formData.libele_categorie.trim()) {
      toast.error('Veuillez entrer un nom de catégorie')
      return
    }
    
    try {
      if (isAddMode) {
        // Ajouter une nouvelle catégorie
        await categoryService.createCategory({
          libele_categorie: formData.libele_categorie,
          icon: formData.icon,
          couleur: formData.couleur
        })
        toast.success('Catégorie ajoutée avec succès!')
        loadCategories()
        closeModal()
      } else {
        // Mettre à jour une catégorie existante
        await categoryService.updateCategory(selectedCategory.categorie_id, {
          libele_categorie: formData.libele_categorie,
          icon: formData.icon,
          couleur: formData.couleur
        })
        toast.success('Catégorie mise à jour avec succès!')
        loadCategories()
        closeModal()
      }
    } catch (error) {
      console.error('Error saving category:', error)
      toast.error('Erreur lors de l\'enregistrement')
    }
  }

  // Filtrer les catégories
  const filteredCategories = categories.filter(cat =>
    cat.libele_categorie.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <AdminLayout>
      <div className="space-y-6 mt-10">
        {/* Header */}
        <div className="bg-white rounded-md border border-slate-200 p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-[#FFD700] to-[#F5C800] rounded-md flex items-center justify-center">
                <svg className="w-8 h-8 text-[#002A4E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl font-semibold text-gray-800">Gestion des Catégories</h1>
                <p className="text-gray-500 mt-1">Gérez et organisez les catégories de la plateforme</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-4xl font-bold text-[#B8860B]">{categories.length}</p>
              <p className="text-gray-500 text-sm">Catégories actives</p>
            </div>
          </div>
        </div>

        {/* Barre de recherche */}
        <div className="bg-white rounded-md border border-slate-200 p-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Rechercher une catégorie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent transition-all"
              />
            </div>
            <button
              onClick={handleAddCategory}
              className="px-6 py-3 bg-gradient-to-r from-[#FFD700] to-[#F5C800] text-[#002A4E] rounded-md transition-all font-medium flex items-center gap-2 whitespace-nowrap"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              Nouvelle Catégorie
            </button>
          </div>
        </div>

        {/* Grille des catÃ©gories */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-[#F5B700] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-500">Chargement des catégories...</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredCategories.map((category) => (
              <div
                key={category.categorie_id}
                onClick={() => handleViewCategory(category)}
                className="bg-white rounded-md border border-slate-200 overflow-hidden hover:border-[#F5B700]/30 transition-all cursor-pointer group"
              >
                {/* En-tête avec icône et couleur */}
                <div 
                  className="h-32 flex items-center justify-center relative overflow-hidden group-hover:scale-105 transition-transform"
                  style={{ 
                    backgroundColor: category.couleur || '#D4AF37',
                    backgroundImage: `linear-gradient(135deg, ${category.couleur || '#D4AF37'} 0%, ${category.couleur || '#D4AF37'}dd 100%)`
                  }}
                >
                  <span className="material-icons text-white" style={{ fontSize: '64px' }}>
                    {safeIcon(category.icon)}
                  </span>
                  {/* Badge ID en haut à droite */}
                  <div className="absolute top-2 right-2 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-md text-white text-xs font-mono">
                    #{category.categorie_id}
                  </div>
                </div>
                
                {/* Contenu */}
                <div className="p-4 text-center">
                  <h3 className="text-lg font-semibold text-gray-800 group-hover:text-[#B8860B] transition-colors mb-2">
                    {category.libele_categorie}
                  </h3>
                  
                  <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span>Active</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Message si aucune catÃ©gorie */}
        {!loading && filteredCategories.length === 0 && (
          <div className="bg-white rounded-md border border-slate-200 p-12 text-center">
            <svg className="w-20 h-20 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Aucune catégorie trouvée</h3>
            <p className="text-gray-500">Essayez de modifier vos critères de recherche</p>
          </div>
        )}
      </div>

      {/* Modal DÃ©tails CatÃ©gorie */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] animate-fadeIn">
          <div className="bg-white rounded-md w-full max-w-2xl m-4 animate-scaleIn flex flex-col max-h-[90vh] border border-slate-200">
            {/* Header - Fixed */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div 
                  className="w-12 h-12 rounded-md flex items-center justify-center "
                  style={{ backgroundColor: formData.couleur }}
                >
                  <span className="material-icons text-white" style={{ fontSize: '32px' }}>{safeIcon(formData.icon)}</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {isAddMode ? 'Ajouter une catégorie' : 'Modifier la catégorie'}
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {isAddMode ? 'Créer une nouvelle catégorie' : 'Modifier les informations'}
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors hover:bg-gray-100 rounded-md p-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body - Scrollable */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-6">
                {/* AperÃ§u visuel */}
                <div 
                  className="rounded-md p-8 flex flex-col items-center relative overflow-hidden"
                  style={{ 
                    background: `linear-gradient(135deg, ${formData.couleur}15 0%, ${formData.couleur}25 100%)`
                  }}
                >
                  <div 
                    className="w-32 h-32 rounded-md flex items-center justify-center mb-4 transform hover:scale-110 transition-transform"
                    style={{ 
                      backgroundColor: formData.couleur,
                      backgroundImage: `linear-gradient(135deg, ${formData.couleur} 0%, ${formData.couleur}dd 100%)`
                    }}
                  >
                    <span className="material-icons text-white" style={{ fontSize: '72px' }}>{safeIcon(formData.icon)}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 text-center">
                    {formData.libele_categorie || 'Nom de la catégorie'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-2">Aperçu de la carte catégorie</p>
                </div>

                {/* Informations dÃ©taillÃ©es */}
                <div className="grid grid-cols-1 gap-4">
                  {/* ID de la catÃ©gorie */}
                  {!isAddMode && (
                    <div className="bg-white border border-slate-200 rounded-md p-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        ID de la catégorie
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="px-4 py-2 bg-gray-100 rounded-md text-gray-800 font-mono text-lg">
                          #{selectedCategory.categorie_id}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Nom de la catÃ©gorie */}
                  <div className="bg-white border border-slate-200 rounded-md p-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nom de la catégorie *
                    </label>
                    <input
                      type="text"
                      value={formData.libele_categorie}
                      onChange={(e) => setFormData({...formData, libele_categorie: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-800 font-medium focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                      placeholder="Ex: Smartphones, Auto-Moto..."
                    />
                  </div>

                  {/* IcÃ´ne */}
                  <div className="bg-white border border-slate-200 rounded-md p-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Icône (Sélectionnez une icône Material)
                    </label>
                    <div className="grid grid-cols-8 gap-2 mb-3 max-h-64 overflow-y-auto p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-md border border-gray-200">
                      {iconOptions.map((icon, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setFormData({...formData, icon: icon})}
                          className={`w-12 h-12 flex items-center justify-center rounded-md transition-all hover:scale-110 ${
 formData.icon === icon 
 ? 'bg-gradient-to-br from-[#FFD700] to-[#F5C800] ring-2 ring-[#002A4E] scale-105' 
 : 'bg-white hover:bg-gray-50 border border-slate-200'
 }`}
                          title={icon}
                        >
                          <span className="material-icons text-gray-700" style={{ fontSize: '24px' }}>{icon}</span>
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 mt-3">
                      <div 
                        className="w-16 h-16 rounded-md flex items-center justify-center text-3xl border-2 "
                        style={{ 
                          backgroundColor: formData.couleur,
                          borderColor: formData.couleur 
                        }}
                      >
                        <span className="material-icons text-white" style={{ fontSize: '32px' }}>{safeIcon(formData.icon)}</span>
                      </div>
                      <input
                        type="text"
                        value={formData.icon}
                        onChange={(e) => setFormData({...formData, icon: e.target.value})}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-md text-gray-800 font-medium focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                        placeholder="Ex: smartphone, home, category..."
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">💡 Astuce : Utilisez les noms d'icônes Material Icons (ex: home, smartphone, category)</p>
                  </div>

                  {/* Couleur */}
                  <div className="bg-white border border-slate-200 rounded-md p-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Couleur de la catégorie
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={formData.couleur}
                        onChange={(e) => setFormData({...formData, couleur: e.target.value})}
                        className="w-16 h-16 rounded-md border-2 border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.couleur}
                        onChange={(e) => setFormData({...formData, couleur: e.target.value})}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-md text-gray-800 font-mono font-medium focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                        placeholder="#D4AF37"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer - Fixed */}
            <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-200 bg-gray-50 rounded-b-md flex-shrink-0">
              <button
                onClick={closeModal}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-all font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                className="px-6 py-2.5 bg-gradient-to-r from-[#FFD700] to-[#F5C800] text-[#002A4E] rounded-md transition-all font-medium flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {isAddMode ? 'Ajouter' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default AdminCategoriesPage

